import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Container from '@/components/PageSheet';
import DiscountField from '@/components/DiscountField';
import { rerunsApi } from '@/services/api';
import { localized } from '@/utils/localizedContent';

/**
 * /rerun/:rerunId — pay for a report support is re-running for you.
 *
 * Reached from an SMS, so it has to explain itself to someone who did not
 * navigate here: what went wrong, what is being redone, and what it costs.
 * The amount is whatever support set for this case, which is why it is read
 * from the server and never computed here.
 *
 * Deliberately NOT part of the consultation checkout: that flow buys a
 * service and hands out a token. This buys one more attempt at a report
 * already paid for once, so it has its own endpoint and its own page.
 */
export default function RerunPayPage() {
  const { rerunId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fa') ? 'fa' : 'en';
  const qc = useQueryClient();

  const [gatewayOpen, setGatewayOpen] = useState(false);
  const [payError, setPayError] = useState('');
  // Only the code travels to the server; it re-quotes the discount against
  // the amount it stored, so the browser never names a price.
  const [discountCode, setDiscountCode] = useState(null);

  const Forward = i18n.dir() === 'rtl' ? ArrowBackIcon : ArrowForwardIcon;

  // The SMS link is remembered at boot so it survives the login detour
  // (main.jsx stores it, OtpPage and PublicRoute both read it). Arriving here
  // is what it was for, so clear it — otherwise the next sign-in on this
  // device reopens a payment page the client already dealt with.
  useEffect(() => {
    try { sessionStorage.removeItem('pendingRerunId'); } catch { /* ignore */ }
  }, []);

  const { data: rerun, isLoading, isError } = useQuery({
    queryKey: ['rerun', rerunId],
    queryFn: () => rerunsApi.getRerun(rerunId),
  });

  const payMut = useMutation({
    mutationFn: (gateway) => rerunsApi.payRerun(rerunId, gateway, discountCode),
    onSuccess: (res) => {
      if (res?.paymentUrl) { window.location.assign(res.paymentUrl); return; }
      // No gateway configured: the server settled it and the job is queued.
      qc.invalidateQueries({ queryKey: ['rerun', rerunId] });
      qc.invalidateQueries({ queryKey: ['my-consultations'] });
    },
    onError: (err) =>
      setPayError(err?.response?.data?.error?.message || t('common.error')),
  });

  const amount = rerun?.amountToman ?? 0;
  const gateways = rerun?.gateways ?? [];
  const money = (v) =>
    new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB').format(v || 0);

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Skeleton variant="rounded" height={220} />
      </Container>
    );
  }

  if (isError || !rerun) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 3 }}>
          <AlertTitle sx={{ fontWeight: 800 }}>{t('rerun.notFound.title')}</AlertTitle>
          {t('rerun.notFound.body')}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/consultations')}>
          {t('rerun.backToConsultations')}
        </Button>
      </Container>
    );
  }

  // Already settled — either they paid a moment ago, or they re-opened the
  // SMS link later. Either way the answer is "it's running", not a pay button.
  if (!rerun.payable) {
    const done = Boolean(rerun.paidAt) || ['queued', 'done'].includes(rerun.status);
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            {done ? (
              <CheckCircleIcon sx={{ fontSize: 44, color: 'success.main', mb: 1 }} />
            ) : (
              <AutorenewIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
            )}
            <Typography fontWeight={800}>
              {done ? t('rerun.settled.title') : t('rerun.closed.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {done ? t('rerun.settled.body') : t('rerun.closed.body')}
            </Typography>
            <Button variant="contained" sx={{ mt: 3 }}
              onClick={() => navigate(rerun.consultationId
                ? `/consultations/${encodeURIComponent(rerun.consultationId)}`
                : '/consultations')}>
              {t('rerun.viewConsultation')}
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
        {t('rerun.title')}
      </Typography>

      {/* Why they are on this page at all. Someone arriving from an SMS has
          no context, and "pay again" without a reason reads like a mistake. */}
      <Alert severity="info" variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        {rerun.kind === 'recapture' ? t('rerun.intro.recapture') : t('rerun.intro.regenerate')}
      </Alert>

      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack spacing={1.25}>
            {rerun.serviceName ? (
              <Row label={t('rerun.service')} value={localized(rerun.serviceName, lang)} />
            ) : null}
            {rerun.clinicName ? (
              <Row label={t('rerun.clinic')} value={localized(rerun.clinicName, lang)} />
            ) : null}
            {rerun.reason ? <Row label={t('rerun.reason')} value={rerun.reason} /> : null}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" color="text.secondary">{t('rerun.amount')}</Typography>
            <Typography fontWeight={800}>
              {t('card.priceToman', { amount: money(amount) })}
            </Typography>
          </Stack>

          <DiscountField
            amountToman={amount}
            context="consultation"
            clinicCode={rerun.clinicCode}
            onChange={setDiscountCode}
            t={t}
            lang={lang}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 2 }}
            disabled={payMut.isPending}
            onClick={() => {
              // With no gateway configured the server settles directly;
              // an empty chooser would be a dead end.
              if (gateways.length === 0) { payMut.mutate(undefined); return; }
              setGatewayOpen(true);
            }}
          >
            {payMut.isPending ? t('common.loading') : t('rerun.pay')}
          </Button>
          {payError && <Alert severity="error" sx={{ mt: 1.5 }}>{payError}</Alert>}
        </CardContent>
      </Card>

      {/* Same chooser as the consultation checkout: people pick by whichever
          card is to hand, so it is asked at the moment of paying. */}
      <Dialog open={gatewayOpen} onClose={() => setGatewayOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>{t('pay.chooseTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('pay.chooseBody')}
          </Typography>
          {discountCode ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('discount.appliedCode', { code: discountCode })}
            </Alert>
          ) : null}
          <Stack spacing={1.25}>
            {gateways.map((g) => (
              <Button
                key={g}
                fullWidth
                size="large"
                variant="outlined"
                disabled={payMut.isPending}
                onClick={() => { setGatewayOpen(false); payMut.mutate(g); }}
                sx={{ justifyContent: 'space-between', py: 1.4, borderRadius: 3 }}
                endIcon={<Forward />}
              >
                <Box sx={{ textAlign: 'start' }}>
                  <Typography fontWeight={800}>{t(`pay.gateway.${g}`)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(`pay.gatewayHint.${g}`)}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGatewayOpen(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

/** One label/value line in the summary card. */
function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'end' }}>{value}</Typography>
    </Stack>
  );
}
